import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { Switch, Typography } from 'antd'
import dayjs from 'dayjs'
import useDirectory from 'hooks/useDirectory'
import { Building } from 'models/Building'
import useSWR from 'swr'
import { AnnotationType, CustomFolder, Folders } from 'types/building'
import Utils from 'utils'
import CommonFunction from 'utils/commonFunction'

import Button from 'components/atoms/Button'
import RightArrowIcon from 'components/icons/RightArrowIcon'
import ViewerHeader from 'components/organisms/Header/ViewerHeader'

import AnotationMemoList from './components/AnnotationMemo/AnotationMemoList'
import ListChildrenFolder from './components/ListChildrenFolder'
import ListFile from './components/ListFile'
import ListFolder from './components/ListFolder'
import MemoList from './components/MemoList'

export default function BuildingFileList() {
  const { id } = useParams()
  const [childrenFolder, setChildrenFolder] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const folderType = searchParams.get('folder')
  const childrenType = searchParams.get('children')
  const childrenFolderType = searchParams.get('children_folder')
  const keywordSearch = searchParams.get('keyword')
  const [isListFile, setIsListFile] = useState(true)
  const breadcrumbState = {
    folder: Boolean(folderType || childrenType),
    children: Boolean(childrenType || folderType),
    children_folder: Boolean(childrenFolderType),
    keyword: Boolean(
      keywordSearch ||
        searchParams.get('category') ||
        searchParams.get('children')
    )
  }

  const {
    navigate,
    getBuiildingType,
    getDirectory,
    getBuiildingFolderList,
    getDirectoryPrimaryColor
  } = useDirectory()

  const { t } = useTranslation()

  const { data: building } = useSWR<Building>(
    {
      url: `/v1/buildings/by-type?id=${id}&building_type=${getBuiildingType()}`
    },
    {
      onError: (err) => {
        Utils.handleErrorNavigate(err, navigate)
      }
    }
  )

  const { data: bcpCustomFolders, isValidating } = useSWR<CustomFolder[]>(
    getDirectory() === 'bcp' ? { url: `v1/bcp/buildings/${id}/folders` } : null
  )

  const { data: listNumberOfFile } = useSWR({
    url: `v1/buildings/categories-files?building_id=${id}`
  })

  // Fetch memo count separately
  const { data: memoCount } = useSWR({
    url: `v1/memos?building_id=${id}&page=1&page_size=1`
  })

  const annotationTypeValues = Object.values(AnnotationType).filter(
    (v) => typeof v === 'number'
  )

  const listFolder = useMemo((): Folders[] => {
    if (getDirectory() === 'bcp') {
      if (!isValidating) {
        return [
          ...getBuiildingFolderList(),
          ...(bcpCustomFolders
            ?.sort((a: any, b: any) =>
              dayjs(a.createdAt).diff(dayjs(b.createdAt))
            )
            .map((item: any) => ({
              id: item.id,
              label: item.name,
              isFileHolder: false,
              isCustomFolder: true,
              children: item.childFolders.map((folder: any) => ({
                id: folder.id,
                categoryId: folder.categoryId,
                label: folder.name,
                isFileHolder: true,
                children: null
              }))
            })) || [])
        ]
      }
      return []
    }
    return getBuiildingFolderList()
  }, [bcpCustomFolders, getBuiildingFolderList, getDirectory, isValidating])

  const convertedListFolder = useMemo(() => {
    if (listNumberOfFile?.data) {
      const keys = Object.keys(listNumberOfFile.data)
      const folders = listFolder.map((folder) => {
        let numberOfFile = 0

        if (folder.id === 40 && memoCount?.pagination?.total !== undefined) {
          numberOfFile = memoCount.pagination?.total
        } else {
          const folderKeys = keys.filter(
            (key) => +key - folder.id >= 0 && +key - folder.id <= 4
          )
          numberOfFile = folderKeys.reduce((count, key) => {
            return count + listNumberOfFile.data[key]
          }, 0)
        }

        return {
          ...folder,
          numberOfFile,
          children:
            folder.children?.map((item) => ({
              ...item,
              numberOfFile: listNumberOfFile.data[item.id] || 0
            })) || []
        }
      })
      return folders
    }
    return []
  }, [listNumberOfFile, listFolder, memoCount])

  const handleBack = (keepFolder = false) => {
    setIsListFile(true)
    const newParams = new URLSearchParams(searchParams)
    if (!keepFolder) newParams.delete('folder')
    newParams.delete('children')
    newParams.delete('children_folder')
    newParams.delete('keyword')
    newParams.delete('category')
    newParams.delete('is_search')
    newParams.delete('ver')
    setSearchParams(newParams)
  }

  const [hoverState, setHoverState] = useState({
    title: false,
    folder: false
  })

  return (
    <div className="h-screen grid grid-rows-[auto_1fr] overflow-x-hidden bg-[#F9FBFF] search-form-premium">
      <ViewerHeader building={building} />

      <div className="grid grid-rows-[auto_auto_1fr] gap-5 p-5 overflow-auto">
        <div className="flex items-center justify-between">
          <div className="text-xl font-bold space-x-2">
            <span
              onClick={() => handleBack()}
              onMouseEnter={() =>
                setHoverState((prev) => ({ ...prev, title: true }))
              }
              onMouseLeave={() =>
                setHoverState((prev) => ({ ...prev, title: false }))
              }
              style={
                breadcrumbState.folder
                  ? { color: getDirectoryPrimaryColor(hoverState.title) }
                  : {}
              }
              className={breadcrumbState.folder ? 'cursor-pointer' : ''}
            >
              {t('building.titleFileList')}
            </span>

            {folderType && (
              <>
                <RightArrowIcon color="black" size={20} />
                <span
                  onClick={() => handleBack(true)}
                  onMouseEnter={() =>
                    setHoverState((prev) => ({ ...prev, folder: true }))
                  }
                  onMouseLeave={() =>
                    setHoverState((prev) => ({ ...prev, folder: false }))
                  }
                  style={
                    breadcrumbState.children ||
                    breadcrumbState.children_folder ||
                    breadcrumbState.keyword
                      ? { color: getDirectoryPrimaryColor(hoverState.folder) }
                      : {}
                  }
                  className={breadcrumbState.children ? 'cursor-pointer' : ''}
                >
                  {/* for default */}
                  {annotationTypeValues.includes(Number(folderType)) &&
                    CommonFunction.getBreadcrumbTextByFolderType(folderType, t)}

                  {/* for custom file */}
                  {!annotationTypeValues.includes(Number(folderType)) &&
                    bcpCustomFolders?.find(
                      (item) => item.id === Number(folderType)
                    )?.name}
                </span>

                {childrenType && (
                  <>
                    <RightArrowIcon color="#000000" size={20} />
                    <span>
                      {/* for default */}
                      {annotationTypeValues.includes(Number(folderType)) &&
                        CommonFunction.getBreadcrumbTextByChidType(
                          childrenType,
                          t,
                          getBuiildingType()
                        )}

                      {/* for custom file */}
                      {!annotationTypeValues.includes(Number(folderType)) &&
                        bcpCustomFolders
                          ?.find((item) => item.id === Number(folderType))
                          ?.childFolders?.find(
                            (item) => item.categoryId === Number(childrenType)
                          )?.name}
                    </span>
                  </>
                )}

                {childrenFolderType && (
                  <>
                    <RightArrowIcon color="#000000" size={20} />
                    <span>{childrenFolder}</span>
                  </>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-1">
            {childrenType &&
              folderType !== '40' &&
              getDirectory() === 'bcp' && (
                <div className="flex items-center gap-2 mr-4">
                  <Typography className="text-[14px] leading-[21px] text-[#021120] font-bold">
                    {isListFile
                      ? t('building.switchFileList')
                      : t('building.switchMemoList')}
                  </Typography>
                  <Switch
                    size="default"
                    checked={isListFile}
                    onChange={setIsListFile}
                  />
                </div>
              )}

            <Button
              size="middle"
              className="building-file-search-bar theme-text-primary theme-border-primary border border-solid bg-white !w-[175px] !h-[51px] rounded-[8px]"
              onClick={() => navigate(`/viewer/buildings/${id}`)}
            >
              360画像に戻る
            </Button>
          </div>
        </div>

        <ListFolder listFolder={convertedListFolder} />

        <ListChildrenFolder listFolder={convertedListFolder} />

        {folderType === '40' && id && <MemoList buildingId={id} />}

        {isListFile && folderType !== '40' && (
          <ListFile
            listFolder={listFolder}
            setChildrenFolder={setChildrenFolder}
          />
        )}

        {id && !isListFile && childrenType && getDirectory() === 'bcp' && (
          <AnotationMemoList
            buildingId={id || ''}
            folderType={folderType || ''}
            childrenType={childrenType || ''}
          />
        )}
      </div>
    </div>
  )
}
